import { useState, useEffect } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { supabase } from '@/lib/supabase/client'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { FORM_ICONS } from '@/lib/utils/lucide-icon-catalog'
import { IconPicker } from '@/components/shared/icon-picker'

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

interface FormSettingsPanelProps {
  formId: string
  projectId: string
}

export function FormSettingsPanel({ formId, projectId }: FormSettingsPanelProps) {
  const user = useAuthStore((s) => s.user)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [icon, setIcon] = useState('')
  const [instanceType, setInstanceType] = useState<'single' | 'repeatable'>('single')
  const [requiredForFinalSubmit, setRequiredForFinalSubmit] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    supabase
      .from('forms')
      .select('title, description, icon, instance_type, required_for_final_submit')
      .eq('id', formId)
      .single()
      .then(({ data }) => {
        if (data) {
          setTitle(parseTitle(data.title))
          setDescription(parseTitle(data.description as unknown))
          setIcon(data.icon ?? '')
          setInstanceType(data.instance_type as 'single' | 'repeatable')
          setRequiredForFinalSubmit(data.required_for_final_submit)
          setLoaded(true)
        }
      })
  }, [formId])

  const saveMutation = useMutation({
    mutationFn: () =>
      supabase
        .from('forms')
        .update({
          title: { fa: title },
          description: description ? { fa: description } : null,
          icon: icon || null,
          required_for_final_submit: requiredForFinalSubmit,
          updated_by: user?.id,
        })
        .eq('id', formId)
        .then(({ error }) => { if (error) throw error }),
  })

  if (!loaded) {
    return (
      <div className="p-4 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-8 bg-muted rounded animate-pulse" />
        ))}
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">تنظیمات فرم</p>

      <div className="space-y-1.5">
        <Label>عنوان فرم</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>

      <div className="space-y-1.5">
        <Label>توضیحات</Label>
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </div>

      <div className="space-y-1.5">
        <Label>آیکون</Label>
        <IconPicker
          value={icon}
          onChange={(key) => setIcon(key)}
          icons={FORM_ICONS}
          clearable={false}
        />
      </div>

      <div className="space-y-1.5">
        <Label>نوع نمونه</Label>
        <Select value={instanceType} onValueChange={(v) => setInstanceType(v as 'single' | 'repeatable')}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single">تکی</SelectItem>
            <SelectItem value="repeatable">تکرارپذیر</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">تغییر پس از انتشار تأثیر روی داده‌های موجود دارد</p>
      </div>

      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <Checkbox
            id="required_final"
            checked={requiredForFinalSubmit}
            onCheckedChange={(v) => setRequiredForFinalSubmit(Boolean(v))}
          />
          <label htmlFor="required_final" className="text-sm cursor-pointer">
            الزامی برای ارسال نهایی
          </label>
        </div>
        <p className="text-xs text-muted-foreground ps-6">
          {requiredForFinalSubmit
            ? instanceType === 'repeatable'
              ? 'این فرم باید حداقل یک بار (یا تعداد تعیین‌شده در حداقل نمونه‌ها) پر شود تا سابقه قابل ارسال نهایی باشد.'
              : 'این فرم باید یک‌بار پر شود تا سابقه قابل ارسال نهایی باشد.'
            : 'این فرم اختیاری است. سابقه بدون پر کردن این فرم هم قابل ارسال نهایی است.'}
        </p>
      </div>

      <Button
        onClick={() => saveMutation.mutate()}
        disabled={saveMutation.isPending}
        size="sm"
        className="w-full gap-1.5"
      >
        <Save className="w-3.5 h-3.5" />
        {saveMutation.isPending ? 'ذخیره...' : 'ذخیره تنظیمات'}
      </Button>
    </div>
  )
}
